import {
  EntityRepresentativesPerson,
} from "./entityRepresentatives";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Cabinet-local draft model for the Add Person modal. Intentionally simpler
 * than the widget's draft: a CO is not the applicant, so there is no
 * `responsible` field — every cabinet-created row is normalized with
 * `responsible: "person"` (see `normalizeEntityRepresentativePerson`).
 */
export type EntityRepresentativeDraft = {
  id: string;
  firstName: string;
  lastName: string;
  representative: boolean;
  ubo: boolean;
  email: string;
  phone: string;
};

export type EntityRepresentativeDraftErrors = Partial<
  Record<"firstName" | "lastName" | "roles" | "email" | "contact", string>
>;

function generateDraftId(): string {
  const cryptoApi = (globalThis as { crypto?: { randomUUID?: () => string } })
    .crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID (e.g. jsdom 16 in tests).
  // The id is a plain string identifier used to match linked_vrs rows; not a
  // security token, so a non-cryptographic random source is sufficient here.
  const hex = (n: number) =>
    Math.floor(Math.random() * Math.pow(16, n))
      .toString(16)
      .padStart(n, "0");
  const variant = (8 + Math.floor(Math.random() * 4)).toString(16);
  return `${hex(8)}-${hex(4)}-4${hex(3)}-${variant}${hex(3)}-${hex(12)}`;
}

export function createEmptyEntityRepresentativeDraft(): EntityRepresentativeDraft {
  return {
    id: generateDraftId(),
    firstName: "",
    lastName: "",
    representative: false,
    ubo: false,
    email: "",
    phone: "",
  };
}

export function isValidRepresentativeEmail(email: string): boolean {
  return EMAIL_REGEX.test(email.trim());
}

/**
 * Validates a cabinet draft. Differs from the widget's validator in that the
 * cabinet has no `responsible` choice, so the email-or-phone rule is always
 * required (not conditional on `responsible === "person"`) and there is no
 * `responsible` error case.
 */
export function validateEntityRepresentativeDraft(
  draft: EntityRepresentativeDraft,
): EntityRepresentativeDraftErrors {
  const errors: EntityRepresentativeDraftErrors = {};
  const email = draft.email.trim();
  const phone = draft.phone.trim();

  if (!draft.firstName.trim()) {
    errors.firstName = "First name is required";
  }
  if (!draft.lastName.trim()) {
    errors.lastName = "Last name is required";
  }
  if (!draft.ubo && !draft.representative) {
    errors.roles = "Select at least one role";
  }
  if (email && !isValidRepresentativeEmail(email)) {
    errors.email = "Invalid email format";
  }
  if (!email && !phone) {
    errors.contact = "Email or phone is required";
  }

  return errors;
}

/**
 * Normalizes a cabinet draft into the wire-format `EntityRepresentativesPerson`
 * the widget already uses. Hard-codes `responsible: "person"` because a CO is
 * never the person who completes KYC. Trims string fields and drops empty
 * email/phone so the saved JSON does not carry empty strings.
 */
export function normalizeEntityRepresentativePerson(
  draft: EntityRepresentativeDraft,
): EntityRepresentativesPerson {
  const firstName = draft.firstName.trim();
  const lastName = draft.lastName.trim();
  const email = draft.email.trim();
  const phone = draft.phone.trim();

  return {
    id: draft.id,
    firstName,
    lastName,
    representative: draft.representative === true,
    ubo: draft.ubo === true,
    responsible: "person",
    ...(email ? { email } : {}),
    ...(phone ? { phone } : {}),
  };
}
